import { useState } from 'react'
import type { ReactNode } from 'react'
import { toLatinDigits } from '../lib/date'
import { Field, Input } from './Field'

/* ------------------------------------------------------------------
   شبا — «IR» ثابت است و کاربر فقط ۲۴ رقم وارد می‌کند
   ------------------------------------------------------------------
   🔴 ۱۴۰۵/۰۸/۰۹ «اجازه نده شماره شبا بیشتر یا کمتر از ۲۴ رقم وارد بشه».

   ⚠️ رقمِ بیست‌وپنجم پذیرفته نمی‌شود و متنِ چسبانده‌شدهٔ بلندتر هم بریده
   نمی‌شود: شبای بریده‌شده می‌تواند حسابِ کس دیگری باشد. پیام می‌گوید چند رقم بود.

   مقدار همیشه خالی یا `IR` + رقم‌هاست؛ ارقام فارسی، فاصله و نشانه‌های نامرئیِ
   متنِ کپی‌شده همین‌جا پاک می‌شوند. قاعدهٔ اصلی روی سرور است (`Iban`).
   ------------------------------------------------------------------ */

const IBAN_DIGITS = 24

const fa = (n: number) => n.toLocaleString('fa-IR')

function digitsOf(value: string): string {
  return toLatinDigits(value).replace(/\D/g, '')
}

function hasValidChecksum(digits: string): boolean {
  // «IR» در رقمِ کنترلی ۱۸ و ۲۷ حساب می‌شود؛ عدد از بازهٔ number بیرون است، پس رقم‌به‌رقم
  let rest = 0

  for (const char of `${digits.slice(2)}1827${digits.slice(0, 2)}`) {
    rest = (rest * 10 + Number(char)) % 97
  }

  return rest === 1
}

/** ایرادِ شبا، یا `null` اگر خالی یا درست باشد. اجباری بودنِ فیلد با خودِ فرم است. */
export function ibanProblem(value: string): string | null {
  const digits = digitsOf(value)

  if (digits === '') return null

  if (digits.length !== IBAN_DIGITS) {
    return `شبا باید ${fa(IBAN_DIGITS)} رقم باشد؛ ${fa(digits.length)} رقم وارد شده.`
  }

  return hasValidChecksum(digits) ? null : 'شبای واردشده معتبر نیست؛ ارقامش را دوباره بررسی کنید.'
}

export interface IbanFieldProps {
  value: string
  onChange: (value: string) => void
  label?: ReactNode
  hint?: ReactNode
  /** پیام خطای سرور */
  error?: string | null
  required?: boolean
  optional?: boolean
  className?: string
}

export function IbanField({
  value,
  onChange,
  label = 'شبا',
  hint,
  error,
  required,
  optional,
  className,
}: IbanFieldProps) {
  const [touched, setTouched] = useState(false)
  const [rejected, setRejected] = useState<number | null>(null)

  const digits = digitsOf(value)
  const problem = ibanProblem(value)
  const complete = digits.length === IBAN_DIGITS

  const shownError =
    error ||
    (rejected !== null
      ? `شبا ${fa(IBAN_DIGITS)} رقم است؛ ${fa(rejected)} رقم پذیرفته نشد.`
      : touched || complete
        ? problem
        : null)

  const shownHint = complete
    ? '✓ شبا معتبر است'
    : digits !== ''
      ? `${fa(digits.length)} از ${fa(IBAN_DIGITS)} رقم`
      : hint

  return (
    <Field
      label={label}
      hint={shownHint}
      error={shownError}
      required={required}
      optional={optional}
      className={className}
    >
      <Input
        dir="ltr"
        inputMode="numeric"
        autoComplete="off"
        /* ⚠️ `suffix` در صفحهٔ راست‌به‌چپ سمتِ چپ می‌نشیند؛ همان‌جا که شبا با IR شروع می‌شود. */
        suffix="IR"
        value={digits}
        placeholder={`${fa(IBAN_DIGITS)} رقم`}
        onBlur={() => setTouched(true)}
        onChange={(event) => {
          const next = digitsOf(event.target.value)

          if (next.length > IBAN_DIGITS) {
            setRejected(next.length)

            return
          }

          setRejected(null)
          onChange(next === '' ? '' : `IR${next}`)
        }}
      />
    </Field>
  )
}
